import Cookies from "universal-cookie";

import { GQLApi } from "./GQLApi";
import { RESTApi } from "./RESTApi";
import { ServerApi } from "./ServerApi";
import { SessionApi } from "./SessionApi";
import type { CodeSandboxApiOptions, Response, RESTRequest } from "./types";
import { onRequest, createRESTRequester } from "./utils";

export * as rest from "./RESTTypes";

export * as gql from "./generated_gql";

let globalSession: SessionApi | undefined;

export { ApiResponseError } from "./utils";

export class CodeSandboxApi {
  gql: GQLApi;
  rest: RESTApi;
  session: SessionApi;
  server: ServerApi;
  private rootApiRequest: RESTRequest;
  constructor(options: CodeSandboxApiOptions) {
    const baseUrl = options.apiUrl;
    const apiVersion = options.apiVersion || "v1";

    const baseRequestOptions = {
      clientType: options.clientType,
      onRequest,

      getBearerToken: () => this.session.bearerToken,
      onResponse: (response: Response) => {
        const cookies = new Cookies(response.cookies);

        if (cookies.get("signedIn") === "false") {
          this.session.unauthorizedResponseReceived();
        }
      },
    };

    const rootRequest = createRESTRequester({
      ...baseRequestOptions,
      baseUrl: baseUrl,
    });

    const apiRequest = createRESTRequester({
      ...baseRequestOptions,
      baseUrl: baseUrl + "/api/" + apiVersion,
    });

    this.rootApiRequest = createRESTRequester({
      ...baseRequestOptions,
      baseUrl: baseUrl + "/api",
    });

    this.server = new ServerApi({
      clientType: options.clientType,
    });

    // Allows multiple instances of CodeSandboxApi with shared
    // session
    if (globalSession) {
      this.session = globalSession;
    } else {
      this.session = new SessionApi({
        baseUrl,
        apiRequest,
        rootApiRequest: this.rootApiRequest,
        rootRequest,
        apiVersion,
        user: options.user,
        useCliAuthentication: options.useCliAuthentication,
      });
    }

    this.gql = new GQLApi({
      apiUrl: options.apiUrl,
      request: this.rootApiRequest,
      session: this.session,
    });

    this.rest = new RESTApi(apiRequest);
  }

  private createQueryPath(path: string, query?: Record<string, string>) {
    if (!query) {
      return path;
    }

    const queryString = Object.keys(query)
      .map((key) => `${key}=${query[key]}`)
      .join("&");

    if (queryString) {
      return `${path}?${queryString}`;
    }

    return path;
  }

  /**
   * Do a generic GET request to the API
   */
  get<T>(options: {
    path: string;
    headers?: Record<string, string>;
    query?: Record<string, string>;
  }) {
    return this.rootApiRequest<T>({
      method: "GET",
      ...options,
      path: this.createQueryPath(options.path, options.query),
    });
  }
  /**
   * Do a generic POST request to the API
   */
  post<T>(options: {
    path: string;
    data?: Record<string, unknown>;
    headers?: Record<string, string>;
    query?: Record<string, string>;
  }) {
    return this.rootApiRequest<T>({
      method: "POST",
      ...options,
      path: this.createQueryPath(options.path, options.query),
    });
  }
  /**
   * Do a generic PUT request to the API
   */
  put<T>(options: {
    path: string;
    data: Record<string, unknown>;
    headers?: Record<string, string>;
    query?: Record<string, string>;
  }) {
    return this.rootApiRequest<T>({
      method: "PUT",
      ...options,
      path: this.createQueryPath(options.path, options.query),
    });
  }
  /**
   * Do a generic PATCH request to the API
   */
  patch<T>(options: {
    path: string;
    data: Record<string, unknown>;
    headers?: Record<string, string>;
    query?: Record<string, string>;
  }) {
    return this.rootApiRequest<T>({
      method: "PATCH",
      ...options,
      path: this.createQueryPath(options.path, options.query),
    });
  }
  /**
   * Do a generic DELETE request to the API
   */
  delete<T>(options: {
    path: string;
    headers?: Record<string, string>;
    query?: Record<string, string>;
  }) {
    return this.rootApiRequest<T>({
      method: "DELETE",
      ...options,
      path: this.createQueryPath(options.path, options.query),
    });
  }
}
